/**
 * Échéances et notifications — chunk 9.AF.1.
 *
 * Tout arrive qualifié du serveur : l'état temporel, le responsable, « qui attend
 * après qui », et la prochaine action. Ce module ne recalcule rien — décision PO, et
 * propriété qui compte : le seuil de « échéance proche » change à un seul endroit.
 */
import type { AppNotification, DeadlineSummary } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

/** Les échéances d'un dossier. */
export function useProjectDeadlines(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "deadlines"],
    queryFn: () => api.get<DeadlineSummary>(`/v1/projects/${projectId}/deadlines`),
    enabled: !!projectId,
    placeholderData: (prev) => prev,
  });
}

/**
 * Les échéances de tout le cabinet — le tableau de bord.
 *
 * Sans filtre de responsable, délibérément : filtrer côté serveur ferait
 * disparaître les échéances sans responsable de tous les tableaux de bord à la
 * fois. Le partage « à moi / sans responsable / l'équipe » se fait à l'affichage,
 * chaque item portant déjà `responsible_user_id`.
 */
export function useTenantDeadlines() {
  return useQuery({
    queryKey: ["deadlines", "tenant"],
    queryFn: () => api.get<DeadlineSummary>("/v1/deadlines"),
    placeholderData: (prev) => prev,
  });
}

/**
 * Ce qui presse chez UN client — 9.BF.1.
 *
 * La fiche entreprise doit répondre à « qu'est-ce qui presse » sur tous les
 * dossiers de ce client. Le filtre est **serveur** (`?company_id=`) : recouper
 * ici la liste des projets et celle des échéances reviendrait à réimplémenter
 * dans le navigateur une notion qui appartient au backend, et à la rendre
 * inaccessible à la CLI comme au MCP.
 */
export function useCompanyDeadlines(companyId: string | undefined) {
  return useQuery({
    queryKey: ["deadlines", "company", companyId],
    queryFn: () => api.get<DeadlineSummary>(`/v1/deadlines?company_id=${companyId!}`),
    enabled: !!companyId,
  });
}

/** Mes alertes. */
export function useNotifications(unreadOnly = false) {
  return useQuery({
    queryKey: ["notifications", { unreadOnly }],
    queryFn: () =>
      api.get<{ items: AppNotification[]; unread_count: number }>(
        `/v1/notifications?unread_only=${unreadOnly ? "true" : "false"}&limit=50`,
      ),
    placeholderData: (prev) => prev,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch<AppNotification>(`/v1/notifications/${id}/read`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ marked_read: number }>("/v1/notifications/read-all", {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

// ---------------------------------------------------------------------------
// Boucle assureur — chunk 9.AG.1
// ---------------------------------------------------------------------------

export type InsurerLoopRecipient = {
  recipient_id: string;
  insurer_code: string;
  status: string;
  responded_at: string | null;
  response_days: number | null;
  submission_id: string | null;
};

export type InsurerLoopRequest = {
  quote_request_id: string;
  title: string | null;
  status: string;
  sent_at: string | null;
  expected_response_by: string | null;
  total: number;
  answered: number;
  declined: number;
  awaiting: number;
  recipients: InsurerLoopRecipient[];
};

export type InsurerLoop = {
  requests: InsurerLoopRequest[];
  answered: number;
  awaiting: number;
};

/** Où en est-on avec les assureurs, pour un dossier. */
export function useInsurerLoop(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "insurer-loop"],
    queryFn: () => api.get<InsurerLoop>(`/v1/projects/${projectId}/insurer-loop`),
    enabled: !!projectId,
    placeholderData: (prev) => prev,
  });
}

/**
 * Consigner qu'un assureur a répondu — ou décliné.
 *
 * Invalide aussi les échéances : c'est le point du chunk. Sans cette invalidation,
 * l'écran continuerait d'afficher « en attente de l'assureur » juste après qu'on
 * ait enregistré sa réponse.
 */
export function useRecordInsurerResponse(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      quoteRequestId: string;
      recipientId: string;
      kind: "respond" | "decline";
      submissionId?: string;
    }) =>
      api.post<Record<string, unknown>>(
        `/v1/quote-requests/${args.quoteRequestId}/recipients/${args.recipientId}/${args.kind}`,
        args.submissionId ? { submission_id: args.submissionId } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["projects", projectId, "insurer-loop"] });
      void qc.invalidateQueries({ queryKey: ["projects", projectId, "deadlines"] });
      void qc.invalidateQueries({ queryKey: ["quote-requests"] });
    },
  });
}
